import { Component, Input } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, pairwise } from 'rxjs';
import { GeneralService } from '../../../shared/services/general.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { InputGeneral } from '../../../shared/inputs/input-general/input-general';
import { InputGeneralSelectComponent } from '../../../shared/inputs/input-general-select/input-general-select.component';
import { InputGeneralTextComponent } from '../../../shared/inputs/input-general-text/input-general-text.component';
import { HybridFormComponent } from '../hybrid-form/hybrid-form.component';
import {
  ClosingData,
  WorkFlowItem,
  WorkFlowLocation,
} from '../../../shared/models/hybrid-model/gps.model';
import { FieldItem } from '../../../shared/models/hybrid-model/filed-input-model';
import { HybridPageService } from '../../../shared/services/hybrid-page.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-gps',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputGeneral,
    InputGeneralSelectComponent,
    InputGeneralTextComponent,
    HybridFormComponent,
    TranslateModule,
  ],
  templateUrl: './gps.component.html',
})
export class GpsComponent {
  @Input() quotationId = 0;
  public localities: any[] = [];
  public workFlowList: WorkFlowItem[] = [];
  public workFlowLocation: WorkFlowLocation | null = null;
  public locationId = 0;
  public requestButton = false;
  public workFlowView = false;
  public errosMess = '';

  public fieldEditClosing: FieldItem[] = [];
  public fieldLocation: FieldItem[] = [];

  public editClosingHybridForm = new FormGroup<Record<string, FormControl>>({});
  public locationHybridForm = new FormGroup<Record<string, FormControl>>({});

  public gpsForm = new FormGroup({
    workFlowTypeId: new FormControl<WorkFlowItem | null>(null, { validators: Validators.required }),
    workFlowId: new FormControl<number>(0, { validators: Validators.required }),
    quantity: new FormControl<number>(1, { validators: Validators.required }),

    location: new FormGroup({
      zipCode: new FormControl<string>('', {
        validators: [Validators.required, Validators.minLength(5), Validators.maxLength(5)],
      }),
      state: new FormControl<string>('', { validators: Validators.required }),
      municipality: new FormControl<string>('', { validators: Validators.required }),
      localities: new FormControl<number | null>(null, { validators: Validators.required }),
      city: new FormControl<string>('', { validators: Validators.required }),
      street: new FormControl<string>('', { validators: Validators.required }),
      exteriorNumber: new FormControl<string>('', { validators: Validators.required }),
      interiorNumber: new FormControl<string | null>(null),
      phone: new FormControl<string>('', { validators: Validators.required }),
      desC_CLIENTE: new FormControl<string>(''),
    }),

    contact: new FormGroup({
      contact: new FormControl<number | null>(null),
      lastName: new FormControl<string>('', { validators: Validators.required }),
      motherLastName: new FormControl<string>('', { validators: Validators.required }),
      firstName: new FormControl<string>('', { validators: Validators.required }),
      contactPhone: new FormControl<string>('', { validators: Validators.required }),
      mobilePhone: new FormControl<string>('', { validators: Validators.required }),
      email: new FormControl<string>('', { validators: [Validators.required, Validators.email] }),
      comments: new FormControl<string>('', { validators: Validators.required }),
    }),
  });

  constructor(
    private hybridPageService: HybridPageService,
    private generalService: GeneralService,
  ) {}

  ngOnInit(): void {
    this.subsForm(); // Suscribir a los cambios del formulario
    this.workFlowLocation = this.buildWorkFlowLocation(); // Actualizar WorkFlowLocation
    this.getInitialGPSData();
  }

  private buildWorkFlowLocation(): WorkFlowLocation {
    // Para Gps Siempre es 2 Cambia dependiendo el proyecto
    const workFlowTypeIdValue = this.gpsForm.get('workFlowTypeId')?.value;
    return {
      closingFieldId: 0,
      componentCatalogId: 0,
      postalCode: this.gpsForm.get('location.zipCode')?.value ?? '',
      quotationId: this.quotationId,
      selectedValue: 0,
      workflowId: workFlowTypeIdValue?.id ?? 0,
      workflowTypeId: 2,
    };
  }

  public getInitialGPSData(): void {
    this.hybridPageService.getInitialGPSData(this.quotationId).subscribe((ress: any) => {
      if (!ress) return;
      this.gpsForm.controls.location.controls.zipCode.setValue(ress.c_P1);
      this.gpsForm.controls.location.controls.phone.setValue(ress.teL1);
      this.gpsForm.controls.location.controls.street.setValue(ress.callE_NUM1);
      this.gpsForm.controls.location.controls.exteriorNumber.setValue(ress.areA1);
      this.gpsForm.controls.location.controls.desC_CLIENTE.setValue(ress.desC_CLIENTE);
      this.workFlowLocation = this.buildWorkFlowLocation();
      this.postalCodeList();
      this.crearGeo();
    });
  }

  public createWorkflowProject(): void {
    const closingData: ClosingData = {
      cellphoneArea: '',
      cellphoneNumber: this.gpsForm.get('contact.mobilePhone')!.value ?? '',
      comment: this.gpsForm.get('contact.comments')!.value ?? '',
      contactArea: '',
      contactTelephone: this.gpsForm.get('contact.contactPhone')!.value ?? '',
      editClosingFields: [],
      email: this.gpsForm.get('contact.email')!.value ?? '',
      firstLastName: this.gpsForm.get('contact.lastName')!.value ?? '',
      locality: Number(this.gpsForm.get('location.localities')!.value),
      location: {
        apartmentNumber: this.gpsForm.get('location.interiorNumber')!.value ?? '',
        areaNumber: '',
        client:
          (this.gpsForm.get('contact.firstName')!.value ?? '') +
          ' ' +
          (this.gpsForm.get('contact.lastName')!.value ?? '') +
          ' ' +
          (this.gpsForm.get('contact.motherLastName')!.value ?? ''),
        postalCode: this.gpsForm.get('location.zipCode')!.value ?? '',
        quotationId: this.quotationId,
        streetName: this.gpsForm.get('location.street')!.value ?? '',
        streetNumber: this.gpsForm.get('location.exteriorNumber')!.value ?? '',
        telephone: this.gpsForm.get('location.phone')!.value ?? '',
      },
      locationEditClosingFields: [],
      locationId: this.locationId,
      name: this.gpsForm.get('contact.firstName')!.value ?? '',
      quantity: this.gpsForm.get('quantity')!.value ?? 0,
      secondLastName: this.gpsForm.get('contact.motherLastName')!.value ?? '',
      switcher: true, // en Datos de ubicación agregar checkbox ver con anguel
      userId: 1,
      workflowId: this.workFlowLocation!.workflowId,
      workflowTypeId: this.workFlowLocation!.workflowTypeId,
    };

    const editClosingFieldsArray = Object.entries(this.editClosingHybridForm.controls).map(
      ([key, control]) => {
        const ctrl = control as AbstractControl;
        return {
          editFieldId: Number(key),
          fileBytes: '',
          fileName: '',
          value: ctrl.value ?? '',
        };
      },
    );
    closingData.editClosingFields = editClosingFieldsArray;

    const locationEditClosingArray = Object.entries(this.locationHybridForm.controls).map(
      ([key, control]) => {
        const ctrl = control as AbstractControl;
        return {
          editFieldId: Number(key),
          fileBytes: '',
          fileName: '',
          value: ctrl.value ?? '',
        };
      },
    );
    closingData.locationEditClosingFields = locationEditClosingArray;

    this.hybridPageService.createWorkflowProject(closingData).subscribe((ress: any) => {});
  }

  public subsForm() {
    this.gpsForm.valueChanges
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, current]) => JSON.stringify(prev) !== JSON.stringify(current)),
      )
      .subscribe(([prev, current]) => {
        // Actualiza el WorkFlowLocation
        this.workFlowLocation = this.buildWorkFlowLocation();

        // solo si cambió el zipCode
        const prevZip = prev?.location?.zipCode ?? '';
        const currZip = current?.location?.zipCode ?? '';
        if (prevZip !== currZip && currZip && currZip.length === 5) {
          this.postalCodeList();
        }

        // solo si cambió el workflow
        const prevWorkflow = prev?.workFlowTypeId;
        const currWorkflow = current?.workFlowTypeId;
        if (prevWorkflow !== currWorkflow && currWorkflow) {
          this.fieldEditClosing = [];
          this.fieldLocation = [];
          this.editClosingHybridForm = new FormGroup({});
          this.locationHybridForm = new FormGroup({});
          this.workFlowEditClosingFieldList();
        }
      });
  }

  public postalCodeList(): void {
    this.hybridPageService.postalCodeList(this.workFlowLocation!).subscribe((ress) => {
      if (!ress) return;
      if (ress.hasErrors) return;
      const control = this.gpsForm.controls.location.controls;
      if (ress.data.city) {
        control.city.setValue(ress.data.city);
      }
      if (ress.data.municipality) {
        control.municipality.setValue(ress.data.municipality);
      }
      if (ress.data.state) {
        control.state.setValue(ress.data.state);
      }
      if (ress.data.localities && ress.data.localities.length) {
        this.localities = ress.data.localities;
      }
      this.locationDetailKey();
    });
  }

  public workFlowEditClosingFieldList(): void {
    this.fieldEditClosing = [];
    this.hybridPageService
      .workFlowEditClosingFieldList(this.workFlowLocation!)
      .subscribe((ress: any) => {
        if (!ress) return;
        this.fieldEditClosing = ress.data;
        this.savedData();
      });
  }

  public locationEditClosingFieldList(): void {
    this.hybridPageService
      .locationEditClosingFieldList(this.workFlowLocation!)
      .subscribe((ress: any) => {
        if (!ress) return;
        this.fieldLocation = ress.data;
      });
  }

  public savedData(): void {
    this.hybridPageService.savedData(this.workFlowLocation!).subscribe((ress: any) => {
      // if (!ress) return;
      // Si SavedData trae datos no se puede crear mas gps
      // si la respuesta de errors es [] mandar a llamar a los inputs faltantes
      // si data es null renderizar los campos faltantes
      if (ress.data == null) {
        this.requestButton = true;
        this.gpsForm.controls.contact.reset();
        this.gpsForm.controls.quantity.reset();
        this.locationEditClosingFieldList();
      } else {
        this.requestButton = false;
        this.gpsForm.controls.contact.controls.contact.setValue(0);
        this.gpsForm.controls.contact.controls.lastName.setValue(ress.data.firstLastName);
        this.gpsForm.controls.contact.controls.motherLastName.setValue(ress.data.secondLastName);
        this.gpsForm.controls.contact.controls.firstName.setValue(ress.data.name);
        this.gpsForm.controls.contact.controls.contactPhone.setValue(ress.data.contactTelephone);
        this.gpsForm.controls.contact.controls.mobilePhone.setValue(ress.data.cellphoneNumber);
        this.gpsForm.controls.contact.controls.email.setValue(ress.data.email);
        this.gpsForm.controls.contact.controls.comments.setValue(ress.data.comment);
        this.gpsForm.controls.contact.controls.contactPhone.setValue(ress.data.contactNumber);
        this.gpsForm.controls.quantity.setValue(ress.data.quantity);
      }
    });
  }

  public editClosingFieldCatalogList(): void {
    this.hybridPageService
      .editClosingFieldCatalogList(this.workFlowLocation!)
      .subscribe((ress: any) => {
        if (!ress) return;
      });
  }

  public locationDetailKey(): void {
    this.hybridPageService.locationDetailKey(this.workFlowLocation!).subscribe((ress: any) => {
      if (!ress.hasErrors) {
        this.locationId = ress.data.id;
      } else {
        this.locationId = 0;
      }
    });
  }

  public getWorkFlowList(): void {
    this.hybridPageService.getWorkFlowList(this.workFlowLocation!).subscribe((ress: any) => {
      if (!ress.hasErrors) {
        this.fieldEditClosing = [];
        this.fieldLocation = [];
        this.editClosingHybridForm = new FormGroup({});
        this.locationHybridForm = new FormGroup({});
        this.workFlowList = ress.data;
        this.gpsForm.controls.workFlowTypeId.setValue(this.workFlowList[0]);
      }
    });
  }

  public crearGeo(): void {
    this.hybridPageService
      .crearGeo({
        apartmentNumber: '',
        areaNumber: '',
        client: this.gpsForm.controls.location.controls.desC_CLIENTE.value ?? '',
        postalCode: this.gpsForm.get('location.zipCode')!.value ?? '',
        quotationId: this.quotationId,
        streetName: this.gpsForm.get('location.street')!.value ?? '',
        streetNumber: '',
        telephone: this.gpsForm.get('location.phone')!.value ?? '',
      })
      .subscribe((ress: any) => {
        // if (ress) {
        if (ress.hasErrors) {
          // Convertir en modal
          this.workFlowView = false;
          this.errosMess = ress.errors[0];
          this.generalService.viewSnacAlert(
            ress.errors[0],
            'error',
            'No se pudo generar automáticamente',
          );
        } else {
          this.workFlowView = true;
          this.getWorkFlowList();
          this.gpsForm.controls.location.controls.zipCode.setValue(ress.data.postalCode);
          this.gpsForm.controls.location.controls.state.setValue(ress.data.state);
          this.gpsForm.controls.location.controls.municipality.setValue(ress.data.municipality);
          this.gpsForm.controls.location.controls.city.setValue(ress.data.city);
          this.localities = ress.data.localities;
          this.locationId = ress.data.id;
          this.gpsForm.controls.location.controls.localities.setValue(ress.data.postalCodeId);
        }
      });
  }

  public valitdForms(): boolean {
    return this.gpsForm.valid && this.editClosingHybridForm.valid && this.locationHybridForm.valid;
  }
}
